import { WEEK_MS } from "./freshness.js";

// The calendar-week boundary for weekly TARGETS (PG-2, 2026-07-13). A weekly
// target is a goal you chase, so the "this week" the meter/coverage scores has
// to reset on a fixed weekday: with a trailing 7-day window the actual dropped
// on its own as sessions aged out mid-week — the "targets don't move as I'd
// expect" report. The reset day is the athlete's choice (Profile.weekStartDay),
// defaulting to Monday (ISO / EU).
//
// ⛔ This is the boundary of the GOAL, and only of it. It is NOT a ruling that
// the body works in weeks — it doesn't, and a muscle's recovery has no
// calendar. The per-muscle ACUTE/CHRONIC state (how long since a muscle was
// stimulated; how much load it carries across weeks) is a different
// measurement, on a window of its own, and it must never reset.
// Antenore, 2026-09-15. 📖 next/measurement-contract.md §8bis.

// 0 = Sunday .. 6 = Saturday, matching JS Date.getDay(). Default Monday: the
// user is in the EU and the app already numbers weeks ISO (Monday-anchored).
export const DEFAULT_WEEK_START_DAY = 1;

/**
 * Epoch-ms of the most recent `weekStartDay` at 00:00 LOCAL time, at or before
 * `nowMs` — i.e. the start of the calendar week that contains `nowMs`.
 *
 * Local (not UTC) on purpose: a training week is anchored to the athlete's wall
 * clock. The returned value is still an absolute instant, so comparing a
 * session's absolute `startedAtMs` against it is correct regardless of zone.
 *
 * DST-correct: it walks back whole CALENDAR days via `setDate` (not a fixed
 * `days * 86_400_000`), so a week that contains a clock change still lands on
 * local midnight of the start day, not an hour off.
 */
export const weekWindowStart = (
  nowMs: number,
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
): number => {
  const d = new Date(nowMs);
  // Days elapsed since the most recent start-of-week (0..6).
  const back = (d.getDay() - weekStartDay + 7) % 7;
  d.setHours(0, 0, 0, 0); // local midnight of today
  d.setDate(d.getDate() - back); // walk back to the week's start day
  return d.getTime();
};

/**
 * Start of the calendar week `weeksAgo` before the current one (0 = current).
 * Anchored on the DST-correct current-week start, then stepped back in whole
 * calendar weeks. Used by the 4-week reference so its buckets align to the same
 * weekday boundary the meter scores on.
 */
export const weekStartNAgo = (
  nowMs: number,
  weekStartDay: number,
  weeksAgo: number,
): number => {
  const d = new Date(weekWindowStart(nowMs, weekStartDay));
  d.setDate(d.getDate() - weeksAgo * 7);
  return d.getTime();
};

/**
 * Which 0-based calendar week `startedAtMs` falls in, counting back from the
 * current one: 0 = this week, 1 = last week. -1 when `startedAtMs` is after
 * `nowMs`; NaN for an unparseable date. No upper bound: callers window it.
 */
export const calendarWeeksAgo = (
  startedAtMs: number,
  nowMs: number,
  weekStartDay: number,
): number => {
  if (startedAtMs > nowMs) return -1;
  // Weeks between the two local week starts, rounded: a DST change in between
  // makes the gap an hour off whole weeks, and must not move the session
  // across a boundary (PG-58). NaN stays NaN.
  const gap =
    weekWindowStart(nowMs, weekStartDay) -
    weekWindowStart(startedAtMs, weekStartDay);
  return Math.round(gap / WEEK_MS);
};

/** Where `nowMs` sits inside its calendar week (§8bis.1). */
export type WeekProgress = {
  /** JS weekday of today, 0 = Sunday .. 6 = Saturday: the host names it. */
  weekday: number;
  /** 0-based day of the week, counted from `weekStartDay`: 0 = the reset day. */
  dayIndex: number;
  /** Whole days left AFTER today: 0 on the last day. */
  daysLeft: number;
};

/**
 * ⭐ §8bis.1, sua parola del 15/09: **una settimana parziale si dichiara
 * parziale.** Di martedì la settimana ha due giorni e la soglia è di sette, e
 * senza questa riga il numero misurava che giorno è, non l'atleta.
 *
 * The CURRENT calendar week is always in progress, so every surface that shows
 * it says how far in it is: the coverage card and the Home targets card read
 * this one helper. ⛔ `weekStartDay` is the ATHLETE's (the coach sees the
 * athlete's week, not their own). Counted on the local calendar like
 * `weekWindowStart`, so a DST change mid-week moves no day.
 */
export const weekProgress = (
  nowMs: number,
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
): WeekProgress => {
  const weekday = new Date(nowMs).getDay();
  const dayIndex = (weekday - weekStartDay + 7) % 7;
  return { weekday, dayIndex, daysLeft: 6 - dayIndex };
};
